import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { TA_STAGES, inputCls } from "./_shared";

export default function TagAlongsAdmin({ episodes }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("__all__");
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const load = () => {
    setLoading(true);
    const params = filter && filter !== "__all__" ? { episode_id: filter } : {};
    api.get("/admin/tag-along", { params }).then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const setStatus = async (id, status) => {
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await api.patch(`/admin/tag-along/${id}`, null, { params: { status } });
      toast.success(`Moved to ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
      load();
    }
  };

  const onDragStart = (id) => (e) => {
    setDragging(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragEnd = () => { setDragging(null); setDragOver(null); };
  const onDragOverCol = (stage) => (e) => { e.preventDefault(); setDragOver(stage); };
  const onDropCol = (stage) => (e) => {
    e.preventDefault();
    const id = dragging || e.dataTransfer.getData("text/plain");
    setDragOver(null);
    setDragging(null);
    if (!id) return;
    const current = items.find((x) => x.id === id);
    if (current && current.status !== stage) setStatus(id, stage);
  };

  const grouped = useMemo(() => {
    const g = Object.fromEntries(TA_STAGES.map((s) => [s.key, []]));
    items.forEach((it) => {
      const key = g[it.status] ? it.status : "received";
      g[key].push(it);
    });
    return g;
  }, [items]);

  const totalPipeline = items.reduce((s, i) => s + Number(i.amount_eur || 0), 0);
  const epTitle = (id) => episodes.find((e) => e.id === id)?.startup_name || id;

  return (
    <>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-2">Tag-alongs</div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tighter">{items.length} interest{items.length === 1 ? "" : "s"} · €{totalPipeline.toLocaleString()} pipeline</h1>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="ta-filter-episode" value={filter} onChange={(e) => setFilter(e.target.value)} className={`h-10 px-3 ${inputCls}`}>
            <option value="__all__">All episodes</option>
            {episodes.map((ep) => <option key={ep.id} value={ep.id}>{ep.startup_name}</option>)}
          </select>
          <button onClick={load} className="px-4 py-2 border border-white/10 text-xs font-mono uppercase tracking-widest hover:border-lime hover:text-lime">Refresh</button>
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 font-mono text-sm uppercase tracking-widest py-16 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center text-white/50" data-testid="ta-admin-empty">No tag-along interests yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="ta-kanban">
          {TA_STAGES.map((s) => {
            const rows = grouped[s.key];
            const stageTotal = rows.reduce((sum, r) => sum + Number(r.amount_eur || 0), 0);
            return (
              <div
                key={s.key}
                data-testid={`ta-column-${s.key}`}
                onDragOver={onDragOverCol(s.key)}
                onDragLeave={() => setDragOver(null)}
                onDrop={onDropCol(s.key)}
                className={`bg-surface border ${dragOver === s.key ? "border-lime" : "border-white/10"} flex flex-col min-h-[300px] transition-colors`}
              >
                <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest text-white/70">{s.label}</span>
                  <span className="text-xs font-mono text-lime tabular-nums">{rows.length} · €{stageTotal.toLocaleString()}</span>
                </div>
                <div className="p-3 space-y-3 flex-1">
                  {rows.map((r) => (
                    <div
                      key={r.id}
                      data-testid={`ta-card-${r.id}`}
                      draggable
                      onDragStart={onDragStart(r.id)}
                      onDragEnd={onDragEnd}
                      className={`bg-black border ${dragging === r.id ? "border-lime opacity-50" : "border-white/10"} p-3 cursor-move hover:border-lime/60 transition-colors`}
                    >
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <div className="font-heading text-sm font-bold uppercase tracking-tight truncate">{r.investor_name}</div>
                        <div className="font-heading text-sm font-black text-lime tabular-nums shrink-0">€{Number(r.amount_eur).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${r.kind === "solo_invest" ? "border-lime text-lime bg-lime/10" : "border-white/30 text-white/70"}`}>
                          {r.kind === "solo_invest" ? "Invest anyway" : "Tag along"}
                        </span>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 truncate">{epTitle(r.episode_id)}</div>
                      </div>
                      {r.company && <div className="text-xs text-white/60 mb-1 truncate">{r.company}</div>}
                      <div className="flex flex-col gap-0.5 text-xs">
                        <a href={`mailto:${r.email}`} className="text-lime hover:underline truncate">{r.email}</a>
                        <a href={`tel:${r.phone}`} className="text-white/70 hover:text-lime truncate">{r.phone}</a>
                        {r.linkedin && <a href={r.linkedin.startsWith("http") ? r.linkedin : `https://${r.linkedin}`} target="_blank" rel="noreferrer" className="text-white/50 hover:text-lime text-[10px] truncate">{r.linkedin}</a>}
                      </div>
                      {r.message && <div className="text-white/60 text-[11px] mt-2 line-clamp-3">{r.message}</div>}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {TA_STAGES.filter((t) => t.key !== r.status).map((t) => (
                          <button
                            key={t.key}
                            data-testid={`ta-move-${r.id}-${t.key}`}
                            onClick={() => setStatus(r.id, t.key)}
                            className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 border border-white/10 text-white/50 hover:border-lime hover:text-lime transition-colors"
                          >
                            → {t.label}
                          </button>
                        ))}
                        <button
                          data-testid={`ta-delete-${r.id}`}
                          onClick={async () => {
                            if (!window.confirm(`Delete tag-along interest from ${r.investor_name}? This cannot be undone.`)) return;
                            const prev = items;
                            setItems((xs) => xs.filter((x) => x.id !== r.id));
                            try {
                              await api.delete(`/admin/tag-along/${r.id}`);
                              toast.success("Deleted");
                            } catch (err) {
                              setItems(prev);
                              toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
                            }
                          }}
                          className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 border border-white/10 text-white/50 hover:border-danger hover:text-danger transition-colors ml-auto"
                        >
                          Delete
                        </button>
                      </div>
                      <div className="text-[9px] font-mono text-white/30 mt-2">{new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                  {rows.length === 0 && (
                    <div className="text-white/20 text-xs font-mono uppercase tracking-widest text-center py-8">Drop here</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
